<template>
  <div class="auth-page">
    <main class="auth-shell">
      <router-link class="auth-brand" to="/">
        <span class="brand-mark" aria-hidden="true">
          <span></span>
        </span>
        <strong>Codex API</strong>
        <em>找回账户密码</em>
      </router-link>

      <section class="auth-card">
        <div class="auth-heading">
          <h1>找回密码</h1>
          <p>通过注册邮箱验证码设置新密码</p>
        </div>

        <el-form ref="resetForm" :model="resetForm" :rules="resetRules" label-position="top">
          <el-form-item label="邮箱" prop="email">
            <el-input v-model.trim="resetForm.email" placeholder="请输入注册邮箱" prefix-icon="el-icon-message" />
          </el-form-item>
          <el-form-item label="图形验证码" prop="captchaCode">
            <div class="captcha-row">
              <el-input
                v-model.trim="resetForm.captchaCode"
                placeholder="请输入4位验证码"
                maxlength="4"
                prefix-icon="el-icon-picture-outline"
              />
              <button class="captcha-image" type="button" title="点击刷新验证码" @click="loadCaptcha">
                <img v-if="captchaImage" :src="captchaImage" alt="验证码" />
                <span v-else>刷新</span>
              </button>
            </div>
          </el-form-item>
          <p class="auth-help">验证码为 4 位 1-9 数字，点击图片可刷新。</p>
          <el-form-item label="邮箱验证码" prop="emailCode">
            <div class="captcha-row email-code-row">
              <el-input
                v-model.trim="resetForm.emailCode"
                placeholder="请输入6位邮箱验证码"
                maxlength="6"
                prefix-icon="el-icon-message"
              />
              <el-button :disabled="countdown > 0 || sendingCode" @click="sendCode">
                {{ countdown > 0 ? `${countdown} 秒后重试` : '发送验证码' }}
              </el-button>
            </div>
          </el-form-item>
          <el-form-item label="新密码" prop="newPassword">
            <el-input
              v-model="resetForm.newPassword"
              placeholder="请输入至少6位密码"
              prefix-icon="el-icon-lock"
              show-password
            />
          </el-form-item>
          <el-form-item label="确认新密码" prop="confirmPassword">
            <el-input
              v-model="resetForm.confirmPassword"
              placeholder="请再次输入新密码"
              prefix-icon="el-icon-lock"
              show-password
              @keyup.enter.native="submitReset"
            />
          </el-form-item>
          <el-button class="auth-submit" type="primary" round :loading="submitting" @click="submitReset">
            重置密码
          </el-button>
          <el-button class="auth-secondary" round @click="$router.push('/login')">
            返回登录
          </el-button>
        </el-form>
      </section>

      <div class="auth-links">
        <router-link to="/">返回首页</router-link>
        <span>想起密码了？<router-link to="/login">返回登录</router-link></span>
      </div>
      <p class="auth-copy">© 2026 Codex API. 保留所有权利。</p>
    </main>
  </div>
</template>

<script>
import { fetchCaptcha, resetPassword, sendPasswordResetCode } from '../api/auth';

const LAST_EMAIL_STORAGE_KEY = 'codex-api-last-email';

export default {
  name: 'ForgotPasswordView',
  data() {
    return {
      submitting: false,
      sendingCode: false,
      countdown: 0,
      countdownTimer: null,
      captchaImage: '',
      resetForm: {
        email: window.localStorage.getItem(LAST_EMAIL_STORAGE_KEY) || '',
        captchaId: '',
        captchaCode: '',
        emailCode: '',
        newPassword: '',
        confirmPassword: ''
      },
      resetRules: {
        email: [
          { required: true, message: '请输入邮箱', trigger: 'blur' },
          { type: 'email', message: '请输入正确的邮箱', trigger: 'blur' }
        ],
        captchaCode: [
          { required: true, message: '请输入验证码', trigger: 'blur' },
          { pattern: /^[1-9]{4}$/, message: '验证码为 4 位 1-9 数字', trigger: 'blur' }
        ],
        emailCode: [
          { required: true, message: '请输入邮箱验证码', trigger: 'blur' },
          { pattern: /^\d{6}$/, message: '邮箱验证码必须是 6 位数字', trigger: 'blur' }
        ],
        newPassword: [
          { required: true, message: '请输入新密码', trigger: 'blur' },
          { min: 6, message: '密码至少 6 个字符', trigger: 'blur' }
        ],
        confirmPassword: [
          { required: true, message: '请确认新密码', trigger: 'blur' },
          { validator: (rule, value, callback) => {
            if (value !== this.resetForm.newPassword) {
              callback(new Error('两次输入的密码不一致'));
              return;
            }
            callback();
          }, trigger: 'blur' }
        ]
      }
    };
  },
  created() {
    this.loadCaptcha();
  },
  beforeDestroy() {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
    }
  },
  methods: {
    async loadCaptcha() {
      try {
        const { data } = await fetchCaptcha();
        if (data && data.code === 0) {
          this.resetForm.captchaId = data.data.captchaId;
          this.resetForm.captchaCode = '';
          this.captchaImage = data.data.imageBase64;
        }
      } catch (error) {
        this.captchaImage = '';
      }
    },
    async sendCode() {
      if (!this.resetForm.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.resetForm.email)) {
        this.$message.warning('请输入正确的邮箱');
        return;
      }
      if (!this.resetForm.captchaId || !/^[1-9]{4}$/.test(this.resetForm.captchaCode)) {
        this.$message.warning('请先填写正确的图形验证码');
        return;
      }
      this.sendingCode = true;
      try {
        const { data } = await sendPasswordResetCode(
          this.resetForm.email,
          this.resetForm.captchaId,
          this.resetForm.captchaCode
        );
        if (!data || data.code !== 0) {
          this.$message.error((data && data.message) || '验证码发送失败');
          await this.loadCaptcha();
          return;
        }
        this.$message.success('验证码已发送，请查收邮件');
        this.startCountdown();
        await this.loadCaptcha();
      } catch (error) {
        this.$message.error('验证码发送失败，请稍后重试');
        await this.loadCaptcha();
      } finally {
        this.sendingCode = false;
      }
    },
    startCountdown() {
      this.countdown = 60;
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
      }
      this.countdownTimer = setInterval(() => {
        this.countdown -= 1;
        if (this.countdown <= 0) {
          clearInterval(this.countdownTimer);
          this.countdownTimer = null;
        }
      }, 1000);
    },
    submitReset() {
      this.$refs.resetForm.validate(async valid => {
        if (!valid) {
          return;
        }
        this.submitting = true;
        try {
          const { data } = await resetPassword(
            this.resetForm.email,
            this.resetForm.emailCode,
            this.resetForm.newPassword
          );
          if (!data || data.code !== 0) {
            this.$message.error((data && data.message) || '密码重置失败');
            return;
          }
          window.localStorage.setItem(LAST_EMAIL_STORAGE_KEY, this.resetForm.email);
          this.$message.success('密码重置成功，请重新登录');
          this.$router.push('/login');
        } catch (error) {
          this.$message.error('密码重置失败，请检查后端服务');
        } finally {
          this.submitting = false;
        }
      });
    }
  }
};
</script>
